import { Action, ThunkAction } from '@reduxjs/toolkit'
import { Logger, Models, Services, Types } from '@sendum/core'
import toast from 'react-hot-toast'
import { AnyAction } from 'redux'

import { DispatchAction, TimelineRootState } from './TimelineStore'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const logger = new Logger('TimelineActions')

export enum TimelineActionTypes {
    SET_SEGMENTS,
    SET_LOADING,
    SET_ID,
    SET_ERROR,
    SET_CONFIG,
    UPDATE_STATE_BY_MQTT
}

export type TActionProps = {
    masterTrackingNo?: string
    isLoading?: boolean
    id?: string
    segments?: Models.Segment[]
    config?: Types.TTimeLineAttribute
    error?: Error
    message?: string
    topic?: string
}

export const setLoading = (isLoading: boolean, id?: string): DispatchAction<TimelineActionTypes, TActionProps> => ({
    type: TimelineActionTypes.SET_LOADING,
    payload: {
        isLoading
    }
})

export const setId = (id: string): DispatchAction<TimelineActionTypes, TActionProps> => ({
    type: TimelineActionTypes.SET_ID,
    payload: {
        id
    }
})

export const setSegments = (
    segments: Models.Segment[] | undefined,
    id: string
): DispatchAction<TimelineActionTypes, TActionProps> => ({
    type: TimelineActionTypes.SET_SEGMENTS,
    payload: {
        segments,
        id
    }
})

export const setConfig = (config?: Types.TTimeLineAttribute): DispatchAction<TimelineActionTypes, TActionProps> => ({
    type: TimelineActionTypes.SET_CONFIG,
    payload: {
        config
    }
})

export const setError = (error: Error): DispatchAction<TimelineActionTypes, TActionProps> => ({
    type: TimelineActionTypes.SET_ERROR,
    payload: {
        error
    }
})

export const updateStateByMqtt = (
    message: string,
    topic: string
): DispatchAction<TimelineActionTypes, TActionProps> => ({
    type: TimelineActionTypes.UPDATE_STATE_BY_MQTT,
    payload: {
        message,
        topic
    }
})

export const getSegments = (
    masterTrackingNo: string | undefined,
    shipmentId: string | undefined,
    withManufacturingLabels: boolean | undefined,
    isSoft: boolean
): ThunkAction<void, TimelineRootState, null, Action<TimelineActionTypes>> => async (dispatch, getState) => {
    await dispatch((setId((masterTrackingNo ?? shipmentId) as string) as unknown) as AnyAction)

    if (!isSoft) {
        await dispatch((setLoading(true, masterTrackingNo ?? shipmentId) as unknown) as AnyAction)
    }

    try {
        let shipment: Models.Shipment | undefined = undefined
        if (shipmentId) {
            shipment = await Services.API.WidgetsApi.getShipmentById(shipmentId, withManufacturingLabels)
        } else if (masterTrackingNo) {
            shipment = await Services.API.WidgetsApi.getShipmentByOrderId(masterTrackingNo)
        }
        if (!shipment) {
            throw new Error('Shipment not found')
        }

        console.debug('getSegments', getState().Timeline.id === ((masterTrackingNo ?? shipmentId) as string))
        if (getState().Timeline.id !== ((masterTrackingNo ?? shipmentId) as string)) {
            return
        }

        await dispatch(
            (setSegments(shipment.segments, masterTrackingNo ?? (shipmentId as string)) as unknown) as AnyAction
        )
        await dispatch((setConfig(shipment.getTimeLineAttributesConfig()) as unknown) as AnyAction)
        if (!isSoft) {
            await dispatch((setLoading(false, masterTrackingNo ?? shipmentId) as unknown) as AnyAction)
        }
        Services.MQTT.subscribe([`shipment/${shipment.id}`])
    } catch (error) {
        logger.error(error)
        dispatch((setError(error as Error) as unknown) as AnyAction)
        if (error instanceof Error) {
            toast.error(error.message)
        }
    } finally {
        await dispatch((setLoading(false) as unknown) as AnyAction)
    }
}
